import { useState, useCallback } from 'react';

export function useLocalStorage<T>(key: string, initialValue?: T, useSessionStorage = false): [T, (value: T) => void] {
  const [localStorage, setStoredValue] = useState<T>(() => {
    if (typeof window === 'undefined') {
      return initialValue;
    }

    try {
      const item = useSessionStorage ? window.sessionStorage.getItem(key) : window.localStorage.getItem(key);
      return item ? JSON.parse(item) : initialValue;
    } catch (error) {
      console.warn(`Error reading localStorage key "${key}":`, error);
      return initialValue;
    }
  });

  const setLocalStorage = useCallback(
    (value: T) => {
      try {
        // Allow value to be a function so we have same API as useState
        const valueToStore = value instanceof Function ? value(localStorage) : value;
        setStoredValue(valueToStore);

        if (typeof window !== 'undefined') {
          useSessionStorage
            ? window.sessionStorage.setItem(key, JSON.stringify(valueToStore))
            : window.localStorage.setItem(key, JSON.stringify(valueToStore));
        }
      } catch (error) {
        console.warn(`Error setting localStorage key "${key}":`, error);
      }
    },
    [key, useSessionStorage, localStorage],
  );

  return [localStorage, setLocalStorage];
}

export default useLocalStorage;
